import { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import { 
  FiAlertCircle, FiDownload, FiSend, FiCalendar, 
  FiPieChart, FiMail, FiSettings, FiUser, FiClock, FiCheckCircle,
  FiTrash2, FiEdit, FiFilter, FiCheck, FiX, FiCreditCard, FiChevronDown, FiChevronUp, FiSearch
} from 'react-icons/fi';
import { FaRupeeSign, FaGraduationCap } from 'react-icons/fa';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line 
} from 'recharts';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import UserContext from '../../Components/UserContext';
import ErrorBoundary from '../../Components/ErrorBoundary';
import ConfirmDialog from '../../Components/ConfirmDialog';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { PDFDownloadLink } from '@react-pdf/renderer';
import InvoicePDF from './InvoicePDF';
import { format } from 'date-fns';

import { CHART_COLORS, DEFAULT_VALUES, TIME_CONSTANTS } from '../../constants/appConstants';
import { transformTransactionData, transformProgramData, transformStudentData, formatName, toTitleCase } from '../../utils/dataTransformers';
import { useDateTimeSettings } from '../../hooks/useDateTimeSettings';
import { formatDate as formatDateUtil, formatDateTime } from '../../utils/dateFormatter';
import { formatCurrency } from '../../utils/currencyFormatter';
import { formatPaymentStatus } from '../../utils/paymentStatusFormatter';

const COLORS = CHART_COLORS.COLORS_ARRAY;

function FeesDetails() {
  const { session } = useContext(UserContext);
  const dateTimeSettings = useDateTimeSettings();
  const [isPWA, setIsPWA] = useState(false);
  
  // Check if user is a consultant
  const trainerServices = (session?.services && session.services.length > 0) ? session.services : (session?.service ? [session.service] : []);
  const servicePartnerTypes = session?.servicePartnerTypes || {};
  const activeService = localStorage.getItem('activeService') || trainerServices[0] || '';
  const activePartnerType = activeService ? (servicePartnerTypes[activeService] || 'instructor') : 'instructor';
  const isConsultant = activePartnerType === 'consultant';
  
  // Helper function to validate and convert MongoDB ObjectId format
  const isValidObjectId = (id) => {
    if (!id) return false;
    // Handle ObjectId instances (from MongoDB)
    if (id && typeof id === 'object' && id.toString) {
      const idStr = id.toString();
      return /^[0-9a-fA-F]{24}$/.test(idStr);
    }
    // Handle string format
    if (typeof id === 'string') {
      return /^[0-9a-fA-F]{24}$/.test(id);
    }
    return false;
  };
  
  // Helper to convert ObjectId to string
  const objectIdToString = (id) => {
    if (!id) return null;
    if (typeof id === 'string') return id;
    if (id && typeof id === 'object' && id.toString) {
      return id.toString();
    }
    return null;
  };
  
  // Use trainerId if available (for business partners), otherwise use session id
  // Only use valid ObjectIds
  const trainerId = useMemo(() => {
    const id = session?.trainerId || session?.id;
    if (!id) {
      // Session might not be loaded yet - don't show error immediately
      return null;
    }
    
    if (!isValidObjectId(id)) {
      console.warn('Invalid trainer ID format:', id, typeof id);
      return null;
    }
    
    // Convert to string if it's an ObjectId instance
    return objectIdToString(id);
  }, [session?.trainerId, session?.id]);
  
  const [activeTab, setActiveTab] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [groupedTransactions, setGroupedTransactions] = useState({});
  const [programs, setPrograms] = useState([]);
  const [students, setStudents] = useState([]);
  const [rawStudentData, setRawStudentData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setTime(Date.now() - DEFAULT_VALUES.DATE_RANGE_DAYS * TIME_CONSTANTS.DAY)),
    endDate: new Date(),
    key: 'selection'
  });
  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
    variant: 'primary'
  });
  
  // Memoize date strings to prevent unnecessary re-renders
  const startDateTimestamp = dateRange.startDate?.getTime();
  const endDateTimestamp = dateRange.endDate?.getTime();
  const startDateStr = useMemo(() => dateRange.startDate?.toISOString(), [dateRange.startDate]);
  const endDateStr = useMemo(() => dateRange.endDate?.toISOString(), [dateRange.endDate]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [programFilter, setProgramFilter] = useState('all');
  const [studentFilter, setStudentFilter] = useState('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [amountRange, setAmountRange] = useState({
    min: '',
    max: ''
  });
  const [hasLateFeeFilter, setHasLateFeeFilter] = useState('all');
  const [reportType, setReportType] = useState('income');
  const [financialReport, setFinancialReport] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [showReminderSettings, setShowReminderSettings] = useState(false);
  const [reminderLogs, setReminderLogs] = useState([]);
  const [reminderLogsPagination, setReminderLogsPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [reminderLogsFilters, setReminderLogsFilters] = useState({
    status: '',
    studentId: '',
    startDate: null,
    endDate: null
  });
  const [selectedReminderLogs, setSelectedReminderLogs] = useState([]);
  const [showReminderLogsFilters, setShowReminderLogsFilters] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [paymentData, setPaymentData] = useState({
    studentId: '',
    programId: '',
    amount: '',
    studentName: '',
    paymentMethod: 'Cash'
  });
  const [refundData, setRefundData] = useState({
    transactionId: '',
    amount: '',
    reason: '',
    isPartial: false
  });
  const [reminderSettings, setReminderSettings] = useState({
    daysBeforeDue: 3,
    daysAfterDue: [1, 3, 7],
    templateId: '',
    active: true
  });
  const [lateFeeSettings, setLateFeeSettings] = useState({
    feeType: 'percentage',
    amount: 5,
    gracePeriod: 5,
    maxFee: 1000
  });
  const [paymentSummary, setPaymentSummary] = useState({
    totalPaid: 0,
    totalDue: 0,
    status: 'pending'
  });
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [paymentPlans, setPaymentPlans] = useState([]);
  const [showAssignPlanModal, setShowAssignPlanModal] = useState(false);
  const [selectedStudentForPlan, setSelectedStudentForPlan] = useState(null);
  const [studentPaymentPlans, setStudentPaymentPlans] = useState({});
  const [showPaymentScheduleModal, setShowPaymentScheduleModal] = useState(null);
  const [showReminderHistoryModal, setShowReminderHistoryModal] = useState(null);
  const [scholarshipApplications, setScholarshipApplications] = useState([]);
  const [expandedTransactions, setExpandedTransactions] = useState(new Set());

  // Group transactions by student or program (memoized callback)
  // Store programs in a ref to avoid dependency issues
  const programsRef = useRef(programs);
  useEffect(() => {
    programsRef.current = programs;
  }, [programs]);

  const groupTransactions = useCallback((data, groupBy) => {
    if (!Array.isArray(data) || data.length === 0) {
      return {};
    }
    
    // Use ref to get latest programs without creating dependency
    const currentPrograms = programsRef.current;
    
    return data.reduce((acc, transaction) => {
      if (!transaction) return acc;
      
      const key = groupBy === 'student' 
        ? (transaction.studentId || transaction.userId || 'unknown')
        : (transaction.programId || transaction.program?._id || 'unknown');
      
      if (!acc[key]) {
        acc[key] = {
          name: groupBy === 'student' 
            ? (transaction.studentName || transaction.student?.name || 'Unknown Student')
            : (currentPrograms.find(p => p._id === transaction.programId || p._id === transaction.program?._id)?.name || transaction.program?.name || 'Unknown Program'),
          transactions: [],
          totalPaid: 0,
          totalPending: 0
        };
      }
      acc[key].transactions.push(transaction);
      
      const amount = Number(transaction.finalAmount || transaction.amount || 0);
      if (transaction.status === 'completed') {
        acc[key].totalPaid += amount;
      } else if (transaction.status === 'pending') {
        acc[key].totalPending += amount;
      }
      return acc;
    }, {});
  }, []); // No dependencies - uses ref for programs

  // Prepare chart data
  const prepareChartData = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }
    
    const monthlyData = {};
    data.forEach(transaction => {
      if (!transaction) return;
      
      // Validate and parse date
      const transactionDate = transaction.date || transaction.createdAt || transaction.dueDate;
      if (!transactionDate) return;
      
      const date = new Date(transactionDate);
      if (isNaN(date.getTime())) {
        console.warn('Invalid date in transaction:', transaction);
        return;
      }
      
      const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthYear]) {
        monthlyData[monthYear] = {
          name: format(date, 'MMM yyyy'),
          income: 0,
          refunds: 0,
          pending: 0
        };
      }
      
      const amount = Number(transaction.finalAmount || transaction.amount || 0);
      if (isNaN(amount)) return;
      
      if (transaction.status === 'completed') {
        monthlyData[monthYear].income += amount;
      } else if (transaction.status === 'refunded') {
        monthlyData[monthYear].refunds += amount;
      } else if (transaction.status === 'pending') {
        monthlyData[monthYear].pending += amount;
      }
    });
    
    return Object.values(monthlyData).sort((a, b) => {
      const dateA = new Date(a.name);
      const dateB = new Date(b.name);
      if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) return 0;
      return dateA - dateB;
    });
  };

  const chartData = useMemo(() => prepareChartData(transactions), [transactions]);

  // Apply additional filters (memoized for performance)
  const filteredTransactionsMemo = useMemo(() => {
    if (!Array.isArray(transactions) || transactions.length === 0) {
      return [];
    }

    let filtered = transactions.filter(tx => tx != null); // Filter out null/undefined

    if (paymentMethodFilter !== 'all') {
      filtered = filtered.filter(tx => tx.paymentMethod === paymentMethodFilter);
    }


    if (!isConsultant && hasLateFeeFilter !== 'all') {
      filtered = filtered.filter(tx => {
        const lateFee = Number(tx.lateFee || 0);
        return hasLateFeeFilter === 'yes' ? lateFee > 0 : lateFee === 0;
      });
    }

    return filtered;
  }, [transactions, paymentMethodFilter, hasLateFeeFilter, isConsultant]);

  // Memoize grouped transactions
  const groupedTransactionsMemo = useMemo(() => {
    return groupTransactions(filteredTransactionsMemo, 'student');
  }, [filteredTransactionsMemo, groupTransactions, isConsultant]);

  // Detect PWA mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkPWA();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Update state when memoized values change
  useEffect(() => {
    setFilteredTransactions(filteredTransactionsMemo);
    setGroupedTransactions(groupedTransactionsMemo);
  }, [filteredTransactionsMemo, groupedTransactionsMemo]);

  const fetchStudentPaymentSummary = useCallback(async (studentId) => {
    try {
      // Sanitize studentId to prevent path traversal attacks
      if (!studentId || typeof studentId !== 'string') {
        console.error('Invalid studentId');
        return;
      }
      
      // Only allow alphanumeric, hyphens, and underscores (MongoDB ObjectId format)
      const sanitizedStudentId = studentId.replace(/[^a-zA-Z0-9\-_]/g, '');
      if (sanitizedStudentId !== studentId || sanitizedStudentId.length === 0) {
        console.error('Invalid studentId format');
        return;
      }
      
      const { data } = await HttpInterceptor.get(`/api/fee-plan/students/payment-summary/${sanitizedStudentId}`);
      setPaymentSummary(data);
    } catch (err) {
      console.error('Failed to load payment summary', err);
    }
  }, []);

  const abortControllerRef = useRef(null);
  const isFetchingRef = useRef(false); // Track if a fetch is in progress
  const fetchTimeoutRef = useRef(null); // Track debounce timeout
  
  // Main data fetch function - not memoized to avoid dependency issues
  const fetchData = useCallback(async (currentTrainerId, currentSearch, currentStatusFilter, 
                                        currentProgramFilter) => {
    // Validate trainerId before making requests
    if (!currentTrainerId) {
      setError('Invalid trainer ID. Please log in again.');
      setIsLoading(false);
      return;
    }
    
    // Prevent duplicate concurrent requests
    if (isFetchingRef.current) {
      return;
    }
    
    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    isFetchingRef.current = true;
    
    setIsLoading(true);
    setError(null);
    try {
      // Fetch programs
      const programPromise = HttpInterceptor.get('/api/programs', {
        params: { trainerId: currentTrainerId },
        signal: abortController.signal
      });

      // Fetch students
      const studentPromise = HttpInterceptor.get('/api/students', {
        params: { trainerId: currentTrainerId },
        signal: abortController.signal
      });

      // Fetch transactions
      const params = { trainerId: currentTrainerId };
      if (currentSearch) params.search = currentSearch;
      if (currentStatusFilter && currentStatusFilter !== 'all') params.status = currentStatusFilter;
      if (currentProgramFilter && currentProgramFilter !== 'all') params.programId = currentProgramFilter;

      const transactionPromise = HttpInterceptor.get('/api/fee-plan/transactions', {
        params,
        signal: abortController.signal
      });

      // Fetch payment plans
      const paymentPlansPromise = HttpInterceptor.get('/api/fee-plan/installments', {
        params: { trainerId: currentTrainerId },
        signal: abortController.signal
      }).catch(() => ({ data: [] })); // Don't fail if endpoint doesn't exist

      // Fetch scholarship applications (only approved ones)
      const scholarshipPromise = HttpInterceptor.get('/api/fee-plan/scholarship-applications', {
        signal: abortController.signal
      }).catch(() => ({ data: [] })); // Don't fail if endpoint doesn't exist

      const [programResponse, studentResponse, transactionResponse, paymentPlansResponse, scholarshipResponse] = await Promise.all([
        programPromise,
        studentPromise,
        transactionPromise,
        paymentPlansPromise,
        scholarshipPromise
      ]);

      // Check if request was cancelled
      if (abortController.signal.aborted) return;

      // Transform API responses using utility functions
      const programsData = transformProgramData(programResponse.data);
      const studentsData = transformStudentData(studentResponse.data);
      const transactionData = transformTransactionData(transactionResponse.data);

      setPrograms(programsData);
      setStudents(studentsData);
      setError(null); // Clear any previous errors
      setPaymentPlans(Array.isArray(paymentPlansResponse.data) ? paymentPlansResponse.data : []);
      
      // Store approved scholarship applications
      const allScholarships = Array.isArray(scholarshipResponse.data) ? scholarshipResponse.data : [];
      const approvedScholarships = allScholarships.filter(app => app.status === 'approved');
      setScholarshipApplications(approvedScholarships);
      
      // Store raw student data for access to nested fields
      const rawStudents = Array.isArray(studentResponse.data) ? studentResponse.data : [];
      setRawStudentData(rawStudents);
      
      // Track which students have payment plans assigned
      const studentPlanMap = {};
      rawStudents.forEach(student => {
        const studentId = student._id || student.planId;
        if (student.paymentPlanId && studentId) {
          studentPlanMap[studentId.toString()] = student.paymentPlanId.toString();
        }
      });
      setStudentPaymentPlans(studentPlanMap);
      
      // Empty transactions is a valid state, not an error
      setTransactions(transactionData || []);
      setFilteredTransactions(transactionData || []);
      setGroupedTransactions(
        transactionData && transactionData.length > 0 
          ? groupTransactions(transactionData, 'student') 
          : {}
      );
      
    } catch (err) {
      // Ignore abort errors - don't log or show errors for canceled requests
      if (err.name === 'AbortError' || abortController.signal.aborted) {
        isFetchingRef.current = false;
        return;
      }
      
      // Handle different error types
      let errorMessage = 'Failed to load data';
      if (err.type === 'network') {
        errorMessage = 'Network error. Please check your connection.';
      } else if (err.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
      } else if (err.status === 403) {
        errorMessage = 'You don\'t have permission to view this data.';
      } else if (err.status >= 500) {
        errorMessage = 'We’re having trouble right now. Please try again in a moment.';
      } else if (err.message) {
        errorMessage = err.message;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setError(errorMessage);
      // Only show toast if it's a server error (HttpInterceptor handles network errors)
      if (err.type !== 'network' && err.status !== 401) {
        toast.error(errorMessage);
      }
    } finally {
      isFetchingRef.current = false;
      if (!abortController.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [groupTransactions]); // groupTransactions is used inside

  // Student filter removed - payment summary effect removed
  
  // Store latest fetchData in a ref to avoid dependency issues
  const fetchDataRefLatest = useRef(fetchData);
  useEffect(() => {
    fetchDataRefLatest.current = fetchData;
  }, [fetchData]);

  // Main data fetch effect - debounced to prevent rapid re-fetches
  useEffect(() => {
    // Don't show error if session is not loaded yet - wait a bit
    if (!trainerId) {
      // Only show error if we have a session object but invalid ID format
      // Don't show error if session is null/undefined (still loading)
      if (session !== null && session !== undefined) {
        // Check if session has an ID but it's invalid
        const hasId = session.id || session.trainerId;
        if (hasId) {
          // We have an ID but validation failed - show error
          setError('Invalid trainer ID format. Please log in again.');
          setIsLoading(false);
        } else {
          // Session exists but no ID - might be loading or invalid session
          setIsLoading(true);
        }
      } else {
        // Session not loaded yet - don't show error, just wait
        setIsLoading(true);
      }
      return;
    }
    
    // Clear any existing timeout
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    
    // Debounce to prevent rapid re-fetches when multiple filters change
    fetchTimeoutRef.current = setTimeout(() => {
      fetchDataRefLatest.current(trainerId, search, statusFilter, programFilter);
    }, 300); // Debounce time to 300ms
    
    // Cleanup: cancel ongoing requests and timeout on unmount or dependency change
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
      // Only abort if a fetch is actually in progress
      if (abortControllerRef.current && isFetchingRef.current) {
        abortControllerRef.current.abort();
        isFetchingRef.current = false;
      }
    };
    // Remove fetchData from dependencies to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainerId, search, statusFilter, programFilter]);

  const reminderAbortControllerRef = useRef(null);
  const isFetchingRemindersRef = useRef(false);
  
  useEffect(() => {
    const fetchReminderData = async () => {
      if (!trainerId || isFetchingRemindersRef.current) {
        return;
      }
      
      // Cancel previous request if still pending
      if (reminderAbortControllerRef.current) {
        reminderAbortControllerRef.current.abort();
      }
      
      const abortController = new AbortController();
      reminderAbortControllerRef.current = abortController;
      isFetchingRemindersRef.current = true;
      
      try {
        const params = {
          trainerId: trainerId,
          page: reminderLogsPagination.page,
          limit: reminderLogsPagination.limit,
          ...(reminderLogsFilters.status && { status: reminderLogsFilters.status }),
          ...(reminderLogsFilters.studentId && { studentId: reminderLogsFilters.studentId }),
          ...(reminderLogsFilters.startDate && { startDate: reminderLogsFilters.startDate.toISOString().split('T')[0] }),
          ...(reminderLogsFilters.endDate && { endDate: reminderLogsFilters.endDate.toISOString().split('T')[0] })
        };
        console.log('[Reminder Logs] Fetching with params:', params);

        // Build array of promises to fetch - skip late fee settings for consultants
        const fetchPromises = [
          HttpInterceptor.get('/api/fee-plan/reminders/logs', {
            params,
            signal: abortController.signal
          }),
          HttpInterceptor.get('/api/fee-plan/reminders/settings', {
            params: { trainerId: trainerId },
            signal: abortController.signal
          })
        ];
        
        // Only fetch late fee settings for instructors (not consultants)
        if (!isConsultant) {
          fetchPromises.push(
            HttpInterceptor.get('/api/fee-plan/settings/late-fee', {
              params: { trainerId: trainerId },
              signal: abortController.signal
            })
          );
        }
        
        const results = await Promise.allSettled(fetchPromises);

        // Check if request was cancelled
        if (abortController.signal.aborted) return;

        // Handle each response individually
        // Helper function to check if error is a 403 (permission denied)
        const isPermissionError = (error) => {
          return error?.status === 403 || error?.response?.status === 403;
        };

        // Index 0: reminder logs
        if (results[0].status === 'fulfilled') {
          const response = results[0].value.data;
          console.log('[Reminder Logs] API Response:', response);
          // Handle both new paginated response and old array response
          if (response && response.logs) {
            console.log('[Reminder Logs] Using paginated format, logs count:', response.logs.length);
            setReminderLogs(response.logs || []);
            setReminderLogsPagination(response.pagination || {
              page: 1,
              limit: 20,
              total: 0,
              totalPages: 0
            });
          } else if (Array.isArray(response)) {
            // Fallback for old API response format
            console.log('[Reminder Logs] Using array format, logs count:', response.length);
            setReminderLogs(response || []);
            setReminderLogsPagination({
              page: 1,
              limit: 20,
              total: response?.length || 0,
              totalPages: 1
            });
          } else {
            console.warn('[Reminder Logs] Unexpected response format:', response);
            setReminderLogs([]);
            setReminderLogsPagination({
              page: 1,
              limit: 20,
              total: 0,
              totalPages: 0
            });
          }
        } else {
          const error = results[0].reason;
          if (!isPermissionError(error)) {
            // Only log non-403 errors (403 is expected for users without business partner access)
            console.error('[Reminder Logs] Failed to load:', error);
            console.error('[Reminder Logs] Error details:', error.response?.data || error.message);
          }
          setReminderLogs([]);
        }

        // Index 1: reminder settings
        if (results[1].status === 'fulfilled' && results[1].value.data) {
          setReminderSettings(results[1].value.data);
        } else if (!isPermissionError(results[1].reason)) {
          console.error('Failed to load reminder settings:', results[1].reason);
        }

        // Index 2: late fee settings (only for instructors)
        if (!isConsultant && results[2] && results[2].status === 'fulfilled' && results[2].value.data) {
          setLateFeeSettings(results[2].value.data);
        } else if (!isConsultant && results[2] && !isPermissionError(results[2].reason)) {
          console.error('Failed to load late fee settings:', results[2].reason);
        }
      } catch (err) {
        // Ignore abort errors
        if (err.name === 'AbortError' || abortController.signal.aborted) {
          isFetchingRemindersRef.current = false;
          return;
        }
        // Only log unexpected errors (not 403 permission errors)
        const isPermissionError = err?.status === 403 || err?.response?.status === 403;
      } finally {
        isFetchingRemindersRef.current = false;
      }
    };

    // Reminders tab is index 2 (0=Transactions, 1=Financial Reports, 2=Reminders, 3=Settings)
    if (activeTab === 2) {
      fetchReminderData();
    }

    return () => {
      if (reminderAbortControllerRef.current) {
        reminderAbortControllerRef.current.abort();
      }
    };
  }, [trainerId, activeTab, reminderLogsPagination.page, reminderLogsPagination.limit, reminderLogsFilters.status, reminderLogsFilters.studentId, reminderLogsFilters.startDate, reminderLogsFilters.endDate]);

  const fetchFinancialReport = async () => {
    try {
      setIsLoading(true);
      const { data } = await HttpInterceptor.get('/api/fee-plan/transactions/report', {
        params: { 
          trainerId: trainerId, 
          reportType,
          startDate: new Date(new Date().setDate(new Date().getDate() - 365)).toISOString(),
          endDate: new Date().toISOString()
        }
      });
      setFinancialReport(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load financial report');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefund = async () => {
    try {
      setIsLoading(true);
      await HttpInterceptor.post(`/api/fee-plan/transactions/${refundData.transactionId}/refund`, {
        amount: refundData.amount,
        reason: refundData.reason,
        isPartial: refundData.isPartial
      });
      toast.success('Refund processed successfully');
      setShowRefundForm(false);
      setRefundData({
        transactionId: '',
        amount: '',
        reason: '',
        isPartial: false
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to process refund');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendInvoice = async (transactionId) => {
    try {
      setIsLoading(true);
      await HttpInterceptor.post(`/api/fee-plan/invoices/${transactionId}/send`, {});
      toast.success('Invoice sent successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send invoice');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendReminder = async (transactionId) => {
    try {
      setIsLoading(true);
      await HttpInterceptor.post(`/api/fee-plan/reminders/${transactionId}`, {});
      toast.success('Reminder sent successfully');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send reminder');
    } finally {
      setIsLoading(false);
    }
  };

  // Payment plan assignment handler
  const handleAssignPaymentPlan = async (paymentPlanId) => {
    if (!selectedStudentForPlan) return;
    try {
      setIsLoading(true);
      await HttpInterceptor.post(`/api/fee-plan/students/${selectedStudentForPlan._id || selectedStudentForPlan.planId}/assign-plan`, {
        paymentPlanId
      });
      toast.success('Payment plan assigned successfully');
      setShowAssignPlanModal(false);
      setSelectedStudentForPlan(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign payment plan');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch payment schedule
  const [paymentSchedule, setPaymentSchedule] = useState(null);
  const fetchPaymentSchedule = async (studentId) => {
    try {
      setIsLoading(true);
      const { data } = await HttpInterceptor.get(`/api/fee-plan/students/${studentId}/payment-schedule`);
      setPaymentSchedule(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch payment schedule');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch reminder history
  const [reminderHistory, setReminderHistory] = useState(null);
  const fetchReminderHistory = async (studentId) => {
    try {
      setIsLoading(true);
      const { data } = await HttpInterceptor.get(`/api/fee-plan/students/${studentId}/reminder-history`);
      setReminderHistory(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch reminder history');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveReminderSettings = async () => {
    try {
      setIsLoading(true);
      await HttpInterceptor.post('/api/fee-plan/reminders/settings', reminderSettings, {
        params: { trainerId: trainerId }
      });
      toast.success('Reminder settings saved successfully');
      setShowReminderSettings(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save reminder settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveLateFeeSettings = async () => {
    try {
      setIsLoading(true);
      await HttpInterceptor.post('/api/fee-plan/settings/late-fee', lateFeeSettings, {
        params: { trainerId: trainerId }
      });
      toast.success('Late fee settings saved successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save late fee settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleProcessPayment = async () => {
    try {
      if (!trainerId) {
        toast.error('Trainer ID is missing. Please log in again.');
        return;
      }
      
      if (!paymentData.studentId || !paymentData.programId || !paymentData.amount) {
        toast.error('Please fill in all required fields');
        return;
      }
      
      setIsLoading(true);
      
      // Ensure studentName is set before sending
      if (!paymentData.studentName && paymentData.studentId) {
        const student = students.find(s => {
          const sId = s._id?.toString() || s.planId?.toString();
          return sId === paymentData.studentId;
        });
        if (student) {
          paymentData.studentName = formatName(`${student.firstName || ''} ${student.lastName || ''}`.trim() || student.name || 'Unknown Student');
        }
      }
      
      const { data } = await HttpInterceptor.post('/api/fee-plan/transactions/process', {
        ...paymentData,
        trainerId: trainerId,
        paymentMethodId: (paymentData.paymentMethod === 'Credit Card' || paymentData.paymentMethod === 'Debit Card') ? 'pm_card_visa' : undefined // Placeholder for card payments
      });
      toast.success('Payment processed successfully');
      setShowPaymentForm(false);
      setPaymentData({
        studentId: '',
        programId: '',
        amount: '',
        studentName: '',
        paymentMethod: 'Cash'
      });
      fetchData();
      if (paymentData.studentId) {
        fetchStudentPaymentSummary(paymentData.studentId);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to process payment');
    } finally {
      setIsLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Invoice No', 'Student', 'Program', 'Amount', 'Late Fee', 
      'Date', 'Status', 'Payment Method', 'Installment Number', 'Scholarship Applied', 'Refunded Amount', 'Discount Breakdown'
    ].join(',');
    
    const rows = filteredTransactions.map(tx => [
      tx.invoiceNo,
      tx.studentName,
      tx.programId?.name || 'Unknown',
      tx.finalAmount,
      tx.lateFee || 0,
      formatDateUtil(tx.date, dateTimeSettings.dateFormat),
      tx.status,
      tx.paymentMethod || 'Other',
      tx.installmentNumber || '',
      tx.scholarshipApplied ? 'Yes' : 'No',
      tx.refundedAmount || 0,
      tx.discountBreakdown ? tx.discountBreakdown.map(d => `${d.type}:${d.name}:${d.amount}`).join(';') : ''
    ].join(','));
    
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `transactions_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
  };

  // Bulk actions handlers
  const handleSelectTransaction = (transactionId) => {
    setSelectedTransactions(prev => {
      if (prev.includes(transactionId)) {
        return prev.filter(id => id !== transactionId);
      } else {
        return [...prev, transactionId];
      }
    });
  };

  const handleSelectAllTransactions = () => {
    if (isSelectAll) {
      setSelectedTransactions([]);
    } else {
      const allIds = filteredTransactions.map(tx => tx._id);
      setSelectedTransactions(allIds);
    }
    setIsSelectAll(!isSelectAll);
  };

  const handleBulkSendReminders = async () => {
    try {
      setIsLoading(true);
      await HttpInterceptor.post('/api/fee-plan/reminders/bulk', {
        transactionIds: selectedTransactions
      });
      toast.success(`Reminders sent to ${selectedTransactions.length} transactions`);
      setSelectedTransactions([]);
      setIsSelectAll(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send bulk reminders');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkExport = () => {
    const selected = filteredTransactions.filter(tx => selectedTransactions.includes(tx._id));
    if (selected.length === 0) {
      toast.warning('No transactions selected for export');
      return;
    }

    const headers = [
      'Invoice No', 'Student', 'Program', 'Amount', 'Late Fee', 
      'Date', 'Status', 'Payment Method', 'Installment Number', 'Scholarship Applied', 'Refunded Amount', 'Discount Breakdown'
    ].join(',');
    
    const rows = selected.map(tx => [
      tx.invoiceNo,
      tx.studentName,
      tx.programId?.name || 'Unknown',
      tx.finalAmount,
      tx.lateFee || 0,
      formatDateUtil(tx.date, dateTimeSettings.dateFormat),
      tx.status,
      tx.paymentMethod || 'Other',
      tx.installmentNumber || '',
      tx.scholarshipApplied ? 'Yes' : 'No',
      tx.refundedAmount || 0,
      tx.discountBreakdown ? tx.discountBreakdown.map(d => `${d.type}:${d.name}:${d.amount}`).join(';') : ''
    ].join(','));
    
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `selected_transactions_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
  };

  // Reminder Logs Functions
  const handleDeleteReminderLog = async (logId) => {
    try {
      await HttpInterceptor.delete(`/api/fee-plan/reminders/logs/${logId}`, {
        params: { trainerId }
      });
      toast.success('Reminder log deleted successfully');
      
      // Calculate if current page will be empty after deletion
      const currentPageLogsCount = reminderLogs.length;
      const remainingOnPage = currentPageLogsCount - 1;
      
      // Determine which page to fetch after deletion
      let targetPage = reminderLogsPagination.page;
      
      // If all logs on current page are deleted, go to previous page or page 1
      if (remainingOnPage <= 0 && reminderLogsPagination.page > 1) {
        targetPage = reminderLogsPagination.page - 1;
      } else if (remainingOnPage <= 0) {
        targetPage = 1;
      }
      
      // Refresh reminder logs with updated page
      const params = {
        trainerId: trainerId,
        page: targetPage,
        limit: reminderLogsPagination.limit,
        ...(reminderLogsFilters.status && { status: reminderLogsFilters.status }),
        ...(reminderLogsFilters.studentId && { studentId: reminderLogsFilters.studentId }),
        ...(reminderLogsFilters.startDate && { startDate: reminderLogsFilters.startDate.toISOString().split('T')[0] }),
        ...(reminderLogsFilters.endDate && { endDate: reminderLogsFilters.endDate.toISOString().split('T')[0] })
      };
      const response = await HttpInterceptor.get('/api/fee-plan/reminders/logs', { params });
      setReminderLogs(response.data.logs || []);
      setReminderLogsPagination(response.data.pagination || {
        ...reminderLogsPagination,
        page: targetPage
      });
      setSelectedReminderLogs([]);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete reminder log');
    }
  };

  const handleBulkDeleteReminderLogs = async () => {
    if (selectedReminderLogs.length === 0) {
      toast.warning('No reminder logs selected');
      return;
    }

    try {
      const response = await HttpInterceptor.post('/api/fee-plan/reminders/logs/bulk-delete', {
        logIds: selectedReminderLogs
      }, {
        params: { trainerId }
      });
      toast.success(response.data.message || `${selectedReminderLogs.length} reminder log(s) deleted successfully`);
      
      // Calculate if current page will be empty after deletion
      const currentPageLogsCount = reminderLogs.length;
      const deletedCount = selectedReminderLogs.length;
      const remainingOnPage = currentPageLogsCount - deletedCount;
      
      // Determine which page to fetch after deletion
      let targetPage = reminderLogsPagination.page;
      
      // If all logs on current page are deleted, go to previous page or page 1
      if (remainingOnPage <= 0 && reminderLogsPagination.page > 1) {
        targetPage = reminderLogsPagination.page - 1;
      } else if (remainingOnPage <= 0) {
        targetPage = 1;
      }
      
      // Refresh reminder logs with updated page
      const params = {
        trainerId: trainerId,
        page: targetPage,
        limit: reminderLogsPagination.limit,
        ...(reminderLogsFilters.status && { status: reminderLogsFilters.status }),
        ...(reminderLogsFilters.studentId && { studentId: reminderLogsFilters.studentId }),
        ...(reminderLogsFilters.startDate && { startDate: reminderLogsFilters.startDate.toISOString().split('T')[0] }),
        ...(reminderLogsFilters.endDate && { endDate: reminderLogsFilters.endDate.toISOString().split('T')[0] })
      };
      const response2 = await HttpInterceptor.get('/api/fee-plan/reminders/logs', { params });
      setReminderLogs(response2.data.logs || []);
      setReminderLogsPagination(response2.data.pagination || {
        ...reminderLogsPagination,
        page: targetPage
      });
      setSelectedReminderLogs([]);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete reminder logs');
    }
  };

  const handleSelectReminderLog = (logId) => {
    setSelectedReminderLogs(prev => {
      if (prev.includes(logId)) {
        return prev.filter(id => id !== logId);
      } else {
        return [...prev, logId];
      }
    });
  };

  const handleSelectAllReminderLogs = () => {
    if (selectedReminderLogs.length === reminderLogs.length) {
      setSelectedReminderLogs([]);
    } else {
      setSelectedReminderLogs(reminderLogs.map(log => log._id));
    }
  };

  const exportReminderLogsToCSV = () => {
    const headers = [
      'Date', 'Student', 'Invoice', 'Amount', 'Status'
    ].join(',');
    
    const rows = reminderLogs.map(log => [
      formatDateTime(log.sentAt, dateTimeSettings.dateFormat, dateTimeSettings.timeFormat),
      log.studentName,
      log.invoiceNo,
      log.amount,
      log.status
    ].join(','));
    
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `reminder_logs_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkDelete = async () => {
    try {
      setIsLoading(true);
      const response = await HttpInterceptor.post('/api/fee-plan/transactions/bulk-delete', {
        transactionIds: selectedTransactions
      });
      toast.success(response.data?.message || `Successfully deleted ${selectedTransactions.length} transaction(s)`);
      setSelectedTransactions([]);
      setIsSelectAll(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete transactions');
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDeleteTransactions = () => {
    setConfirmConfig({
      isOpen: true,
      title: 'Delete Transactions',
      message: `Are you sure you want to delete ${selectedTransactions.length} transaction(s)? This action cannot be undone.`,
      variant: 'danger',
      onConfirm: handleBulkDelete
    });
  };

  useEffect(() => {
    setShowBulkActions(selectedTransactions.length > 0);
  }, [selectedTransactions]);

  return (
    <ErrorBoundary>
      <div className={`min-h-screen ${isPWA ? 'bg-gradient-to-b from-orange-50/40 via-amber-50/30 to-white' : 'bg-gray-50'} py-12 px-4 sm:px-6 lg:px-8`}>
        {/* Header Section */}
        <div className={`relative ${isPWA ? 'bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50' : 'bg-gradient-to-r from-orange-600 to-amber-700'} ${isPWA ? 'text-gray-900' : 'text-white'} rounded-2xl ${isPWA ? 'mb-6 sm:mb-8' : 'mb-8'} overflow-hidden`}>
          {isPWA && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400"></div>
          )}
          {!isPWA && (
            <div className="absolute inset-0 bg-black opacity-20"></div>
          )}
          <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isPWA ? 'py-8 sm:py-10 pb-12 sm:pb-14' : 'py-12'}`}>
            <h1 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-4xl'} font-bold ${isPWA ? 'text-orange-800' : ''} ${isPWA ? 'mb-3' : 'mb-2'} flex items-center gap-2`}>
              <FiCreditCard className={isPWA ? 'text-orange-600' : 'text-white'} />
              Fees
            </h1>
            <p className={`${isPWA ? 'text-sm sm:text-base text-orange-700' : 'text-white/90'}`}>
              {isConsultant ? 'Manage your service fee transactions and payment records' : 'Manage your transactions and payment records'}
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg flex items-start gap-3">
            <FiAlertCircle className="mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium">Error</h3>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}


        <Tabs selectedIndex={activeTab} onSelect={index => setActiveTab(index)}>
          <TabList className="flex border-b border-gray-200 mb-6">
            <Tab className="px-4 py-2 font-medium text-sm focus:outline-none" selectedClassName="border-b-2 border-orange-500 text-orange-600">
              <div className="flex items-center gap-2">
                <FaRupeeSign /> {isConsultant ? 'Payments' : 'Transactions'}
              </div>
            </Tab>
            <Tab className="px-4 py-2 font-medium text-sm focus:outline-none" selectedClassName="border-b-2 border-orange-500 text-orange-600">
              <div className="flex items-center gap-2">
                <FiPieChart /> Financial Reports
              </div>
            </Tab>
            <Tab className="px-4 py-2 font-medium text-sm focus:outline-none" selectedClassName="border-b-2 border-orange-500 text-orange-600">
              <div className="flex items-center gap-2">
                <FiMail /> Reminders
              </div>
            </Tab>
            {!isConsultant && (
              <Tab className="px-4 py-2 font-medium text-sm focus:outline-none" selectedClassName="border-b-2 border-orange-500 text-orange-600">
                <div className="flex items-center gap-2">
                  <FiSettings /> Settings
                </div>
              </Tab>
            )}
          </TabList>

          <TabPanel>
            <div className="mb-6 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">{isConsultant ? 'Consultation Payments' : 'Payment Transactions'}</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowPaymentForm(!showPaymentForm)}
                  className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg"
                >
                  <FaRupeeSign size={18} />
                  {showPaymentForm ? 'Cancel' : (isConsultant ? 'New Record' : 'New Payment')}
                </button>
              </div>
            </div>

            {/* Payment Form */}
            {showPaymentForm && (
              <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{isConsultant ? 'Process New Record' : 'Process New Payment'}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{isConsultant ? 'Client' : 'Student'}</label>
                    <select
                      value={paymentData.studentId || ''}
                      onChange={async (e) => {
                        const studentId = e.target.value;
                        if (!studentId) {
                          setPaymentData({ 
                            ...paymentData, 
                            studentId: '',
                            studentName: '',
                            programId: '',
                            amount: ''
                          });
                          return;
                        }
                        
                        // Find student by _id or planId
                        const student = students.find(s => {
                          const sId = s._id?.toString() || s.planId?.toString();
                          return sId === studentId;
                        });
                        
                        // Get raw student data for accurate payment info
                        const rawStudent = rawStudentData.find(rs => {
                          const rsId = rs._id?.toString() || rs.planId?.toString();
                          return rsId === studentId;
                        });
                        
                        // Calculate pending amount from raw student data
                        let pendingAmount = 0;
                        
                        // Get program ID to look up program price
                        let programIdForLookup = null;
                        if (rawStudent?.program?.programId) {
                          programIdForLookup = typeof rawStudent.program.programId === 'object' 
                            ? (rawStudent.program.programId._id?.toString() || rawStudent.program.programId.toString())
                            : rawStudent.program.programId.toString();
                        } else if (student?.programId) {
                          programIdForLookup = typeof student.programId === 'object'
                            ? (student.programId._id?.toString() || student.programId.toString())
                            : student.programId.toString();
                        }
                        
                        // Get program price from programs array
                        let programPrice = 0;
                        if (programIdForLookup && programs.length > 0) {
                          const program = programs.find(p => {
                            const pId = p._id?.toString();
                            return pId === programIdForLookup;
                          });
                          if (program) {
                            programPrice = program.price || program.currentPrice || program.basePrice || 0;
                          }
                        }
                        
                        if (rawStudent) {
                          const rawPayment = rawStudent.payment;
                          
                          // Prioritize dueAmount
                          if (rawPayment?.dueAmount !== undefined && rawPayment?.dueAmount !== null && rawPayment.dueAmount > 0) {
                            pendingAmount = rawPayment.dueAmount;
                          } else {
                            // Calculate from totalAmount - paidAmount
                            // Use program price if totalAmount is 0
                            const totalAmount = rawPayment?.totalAmount || 
                                              rawStudent.program?.finalPrice || 
                                              rawStudent.program?.basePrice || 
                                              programPrice ||
                                              student?.programDetails?.price || 
                                              0;
                            const paidAmount = rawPayment?.paidAmount || 0;
                            pendingAmount = Math.max(0, totalAmount - paidAmount);
                          }
                        } else if (student?.payment) {
                          // Fallback to transformed student data
                          const totalAmount = student.payment.totalAmount || programPrice || student.programDetails?.price || 0;
                          const paidAmount = student.payment.paidAmount || 0;
                          pendingAmount = Math.max(0, totalAmount - paidAmount);
                        } else if (programPrice > 0) {
                          // If no payment data but we have program price, use that
                          pendingAmount = programPrice;
                        }
                        
                        // If still 0, try to fetch from API
                        if (pendingAmount === 0 && studentId) {
                          try {
                            const sanitizedStudentId = studentId.replace(/[^a-zA-Z0-9\-_]/g, '');
                            if (sanitizedStudentId === studentId && sanitizedStudentId.length > 0) {
                              const { data } = await HttpInterceptor.get(`/api/fee-plan/students/payment-summary/${sanitizedStudentId}`);
                              // Check summary.totalPending first (this is what the API returns)
                              if (data?.summary?.totalPending > 0) {
                                pendingAmount = data.summary.totalPending;
                              } else if (data?.totalDue > 0) {
                                pendingAmount = data.totalDue;
                              } else if (data?.totalPaid !== undefined && data?.totalAmount !== undefined) {
                                pendingAmount = Math.max(0, data.totalAmount - data.totalPaid);
                              } else if (programPrice > 0) {
                                // If API returns 0 but we have program price, use that as pending
                                pendingAmount = programPrice;
                              }
                            }
                          } catch (err) {
                            console.error('[Payment Form] Failed to fetch payment summary for amount calculation:', err);
                            // Fallback to program price if API fails
                            if (programPrice > 0) {
                              pendingAmount = programPrice;
                            }
                          }
                        }
                        
                        // Final fallback: use program price if we still have 0
                        if (pendingAmount === 0 && programPrice > 0) {
                          pendingAmount = programPrice;
                        }
                        
                        // Final fallback: use program price if we still have 0
                        if (pendingAmount === 0 && programPrice > 0) {
                          pendingAmount = programPrice;
                        }
                        
                        // Get student name with fallback
                        let studentName = '';
                        if (student) {
                          studentName = formatName(`${student.firstName || ''} ${student.lastName || ''}`.trim() || student.name || 'Unknown Student');
                        }
                        
                        setPaymentData({ 
                          ...paymentData, 
                          studentId: studentId,
                          studentName: studentName,
                          programId: student?.programId ? (student.programId._id?.toString() || student.programId.toString()) : '',
                          amount: pendingAmount > 0 ? pendingAmount.toFixed(2) : ''
                        });
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="">Select {isConsultant ? 'Client' : 'Student'}</option>
                      {students.length > 0 ? (
                        students
                          .filter(student => {
                            if (!student) return false;
                            // Check enrollment status from raw student data if available
                            const rawStudent = rawStudentData.find(rs => {
                              const rsId = rs._id?.toString() || rs.planId?.toString();
                              const sId = student._id?.toString() || student.planId?.toString();
                              return rsId === sId;
                            });
                            const enrollmentStatus = rawStudent?.enrollment?.status;
                            // Only show active/approved students (not pending)
                            if (enrollmentStatus) {
                              return enrollmentStatus === 'active';
                            }
                            // Fallback to status field
                            const status = student.status || (student.isActive === false ? 'inactive' : 'active');
                            return status === 'active' && student.isActive !== false;
                          })
                          .map(student => {
                            const studentId = student._id?.toString() || student.planId?.toString() || '';
                            const studentName = formatName(`${student.firstName || ''} ${student.lastName || ''}`.trim() || student.name || 'Unknown Student');
                            return (
                              <option key={studentId} value={studentId}>
                                {studentName}
                              </option>
                            );
                          })
                      ) : (
                        <option value="" disabled>No {isConsultant ? 'clients' : 'students'} available</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Program</label>
                    <select
                      value={paymentData.programId}
                      onChange={(e) => setPaymentData({ ...paymentData, programId: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="">Select Program</option>
                      {programs.map(program => (
                        <option key={program._id} value={program._id}>{toTitleCase(program.name)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                    <input
                      type="number"
                      value={paymentData.amount}
                      onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{isConsultant ? 'Method' : 'Payment Method'}</label>
                    <select
                      value={paymentData.paymentMethod}
                      onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="Credit Card">Credit/Debit Card</option>
                      <option value="Debit Card">Debit Card</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Cash">Cash</option>
                      <option value="UPI">UPI</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <button
                      onClick={handleProcessPayment}
                      disabled={!paymentData.studentId || !paymentData.programId || !paymentData.amount}
                      className="w-full flex justify-center items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                    >
                      {isConsultant ? 'Process Record' : 'Process Payment'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Refund Form */}
            {showRefundForm && (
              <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Process Refund</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transaction</label>
                    <select
                      value={refundData.transactionId}
                      onChange={(e) => {
                        const tx = transactions.find(t => t._id === e.target.value);
                        setRefundData({ 
                          ...refundData, 
                          transactionId: e.target.value,
                          amount: tx ? tx.finalAmount : ''
                        });
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="">Select Transaction</option>
                      {transactions.filter(t => t.status === 'completed').map(tx => (
                        <option key={tx._id} value={tx._id}>
                          {tx.invoiceNo} - {tx.studentName} - {formatCurrency(tx.finalAmount)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                    <input
                      type="number"
                      value={refundData.amount}
                      onChange={(e) => setRefundData({ ...refundData, amount: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="0.00"
                      disabled={!refundData.isPartial}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                    <input
                      type="text"
                      value={refundData.reason}
                      onChange={(e) => setRefundData({ ...refundData, reason: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Refund reason"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="partialRefund"
                      checked={refundData.isPartial}
                      onChange={(e) => setRefundData({ ...refundData, isPartial: e.target.checked })}
                      className="h-4 w-4 text-orange-600 focus:ring-teal-500 border-gray-300 rounded"
                    />
                    <label htmlFor="partialRefund" className="ml-2 block text-sm text-gray-700">
                      Partial Refund
                    </label>
                  </div>
                  <div className="md:col-span-2 flex justify-end gap-3">
                    <button
                      onClick={() => setShowRefundForm(false)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleRefund}
                      disabled={!refundData.transactionId || !refundData.reason || (refundData.isPartial && !refundData.amount)}
                      className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg disabled:opacity-50"
                    >
                      Process Refund
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className={`mb-6 ${isPWA ? 'bg-gradient-to-br from-white to-orange-50/30 border-2 border-orange-200/50 shadow-xl' : 'bg-white rounded-xl shadow-sm border border-gray-200'} ${isPWA ? 'rounded-2xl' : ''} overflow-hidden`}>
              {/* Filter Header - Always Visible */}
              <div 
                className={`${isPWA ? 'p-3 sm:p-4' : 'p-6'} flex items-center justify-between cursor-pointer ${isPWA ? 'hover:bg-orange-50/50' : ''} transition-colors`}
                onClick={() => isPWA && setShowFilters(!showFilters)}
              >
                <div className="flex items-center gap-3">
                  <div className={`${isPWA ? 'bg-gradient-to-br from-orange-100 to-amber-100 p-2 rounded-lg' : ''}`}>
                    <FiFilter className={isPWA ? 'text-orange-600' : 'text-gray-600'} size={isPWA ? 18 : 20} />
                  </div>
                  <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} font-medium ${isPWA ? 'text-orange-800' : 'text-gray-900'}`}>Filter Transactions</h3>
                  {(search !== '' || statusFilter !== 'all' || programFilter !== 'all' || paymentMethodFilter !== 'all' || (!isConsultant && hasLateFeeFilter !== 'all')) && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-sm' : 'bg-orange-100 text-orange-800'}`}>
                      Active
                    </span>
                  )}
                </div>
                {isPWA && (
                  <FiChevronDown className={`text-orange-600 transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} />
                )}
              </div>

              {/* Filter Content - Collapsible in PWA */}
              <div className={`${isPWA ? (showFilters ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0') : ''} transition-all duration-300 overflow-hidden`}>
                <div className={`${isPWA ? 'px-3 sm:px-4 pb-3 sm:pb-4' : 'p-6 pt-0'}`}>
                  {/* Search - Always visible in PWA, or in first row on desktop */}
                  <div className={`${isPWA ? 'mb-3' : ''}`}>
                    <label className={`block text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1.5`}>Search</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiSearch className={isPWA ? 'text-orange-400' : 'text-gray-400'} size={16} />
                      </div>
                      <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={isConsultant ? "Search by client or invoice" : "Search by student or invoice"}
                        className={`w-full ${isPWA ? 'pl-9 pr-3 py-2 border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'pl-10 pr-3 py-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                      />
                    </div>
                  </div>

                  {/* Filter Grid - Compact in PWA */}
                  <div className={`grid ${isPWA ? 'grid-cols-2 gap-2 sm:gap-3' : (isConsultant ? 'grid-cols-1 md:grid-cols-3 gap-4' : 'grid-cols-1 md:grid-cols-4 gap-4')} mt-4`}>
                    <div>
                      <label className={`block text-xs sm:text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>{isConsultant ? 'Service' : 'Program'}</label>
                      <select
                        value={programFilter}
                        onChange={(e) => setProgramFilter(e.target.value)}
                        className={`w-full ${isPWA ? 'px-2 py-1.5 text-xs sm:text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'px-3 py-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                      >
                        <option value="all">{isConsultant ? 'All Services' : 'All Programs'}</option>
                        {programs.map(program => (
                          <option key={program._id} value={program._id}>{program.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={`block text-xs sm:text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>Status</label>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className={`w-full ${isPWA ? 'px-2 py-1.5 text-xs sm:text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'px-3 py-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                      >
                        <option value="all">All Statuses</option>
                        <option value="completed">Completed</option>
                        <option value="pending">Pending</option>
                        <option value="failed">Failed</option>
                        <option value="refunded">Refunded</option>
                      </select>
                    </div>
                    <div>
                      <label className={`block text-xs sm:text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>{isConsultant ? 'Method' : 'Payment Method'}</label>
                      <select
                        value={paymentMethodFilter}
                        onChange={(e) => setPaymentMethodFilter(e.target.value)}
                        className={`w-full ${isPWA ? 'px-2 py-1.5 text-xs sm:text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'px-3 py-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                      >
                        <option value="all">All Methods</option>
                        <option value="card">Card</option>
                        <option value="bank">Bank Transfer</option>
                        <option value="cash">Cash</option>
                        <option value="upi">UPI</option>
                      </select>
                    </div>
                    {!isConsultant && (
                      <div>
                        <label className={`block text-xs sm:text-sm font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>Late Fee</label>
                        <select
                          value={hasLateFeeFilter}
                          onChange={(e) => setHasLateFeeFilter(e.target.value)}
                          className={`w-full ${isPWA ? 'px-2 py-1.5 text-xs sm:text-sm border-2 border-orange-200 focus:border-orange-400 focus:ring-orange-400' : 'px-3 py-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                        >
                          <option value="all">All</option>
                          <option value="yes">With Late Fee</option>
                          <option value="no">Without Late Fee</option>
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Reset Button */}
                  {(search !== '' || statusFilter !== 'all' || programFilter !== 'all' || paymentMethodFilter !== 'all' || (!isConsultant && hasLateFeeFilter !== 'all')) && (
                    <div className={`mt-3 ${isPWA ? 'pt-3 border-t border-orange-200/50' : ''}`}>
                      <button
                        onClick={() => {
                          setSearch('');
                          setStatusFilter('all');
                          setProgramFilter('all');
                          setPaymentMethodFilter('all');
                          if (!isConsultant) {
                            setHasLateFeeFilter('all');
                          }
                        }}
                        className={`w-full ${isPWA ? 'px-3 py-2 text-xs sm:text-sm bg-gradient-to-r from-orange-100 to-amber-100 hover:from-orange-200 hover:to-amber-200 text-orange-700 border-2 border-orange-200' : 'px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700'} rounded-lg transition-all duration-200 ${isPWA ? 'font-medium shadow-sm' : ''}`}
                      >
                        Reset Filters
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bulk Actions Bar */}
            {showBulkActions && (
              <div className="mb-4 bg-orange-50 border border-orange-200 rounded-lg p-3 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <span className="text-orange-700 font-medium">
                    {selectedTransactions.length} transaction{selectedTransactions.length !== 1 ? 's' : ''} selected
                  </span>
                  <button 
                    onClick={() => {
                      setSelectedTransactions([]);
                      setIsSelectAll(false);
                    }}
                    className="text-orange-600 hover:text-orange-800 text-sm flex items-center gap-1"
                  >
                    <FiX size={14} /> Clear
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleBulkSendReminders}
                    className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 rounded-lg text-sm"
                  >
                    <FiSend size={14} /> Send Reminders
                  </button>
                  <button
                    onClick={handleBulkExport}
                    className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 rounded-lg text-sm"
                  >
                    <FiDownload size={14} /> Export Selected
                  </button>
                  <button
                    onClick={confirmDeleteTransactions}
                    className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-sm"
                  >
                    <FiTrash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            )}

            {/* Grouped Transactions View */}
            <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">{isConsultant ? 'Payment History by Client' : 'Payment History by Student'}</h3>
                <div className="flex gap-2">
                  <button
                    onClick={exportToCSV}
                    className="flex items-center gap-2 bg-orange-100 hover:bg-orange-200 text-orange-700 px-3 py-2 rounded-lg text-sm"
                  >
                    <FiDownload size={16} />
                    Export CSV
                  </button>
                  <PDFDownloadLink
                    document={<InvoicePDF transactions={filteredTransactions} />}
                    fileName={`transactions_${new Date().toISOString().slice(0,10)}.pdf`}
                    className="flex items-center gap-2 bg-orange-100 hover:bg-orange-200 text-orange-700 px-3 py-2 rounded-lg text-sm"
                  >
                    <FiDownload size={16} />
                    Export PDF
                  </PDFDownloadLink>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center py-12">Loading transactions...</div>
              ) : (
                <div className="space-y-4">
                  {/* Show only approved (active) students, even if they have no transactions */}
                  {students
                    .filter(s => {
                      if (!s) return false;
                      // Check enrollment status from raw student data if available
                      const rawStudent = rawStudentData.find(rs => 
                        (rs._id?.toString() || rs.planId?.toString()) === (s._id?.toString() || s.planId?.toString())
                      );
                      const enrollmentStatus = rawStudent?.enrollment?.status;
                      // Only show active/approved students (not pending)
                      if (enrollmentStatus) {
                        return enrollmentStatus === 'active';
                      }
                      // Fallback to status field
                      const status = s.status || (s.isActive === false ? 'inactive' : 'active');
                      return status === 'active' && s.isActive !== false;
                    })
                    .map(student => {
                      const studentId = student._id || student.planId;
                      const group = groupedTransactions[studentId] || {
                        name: formatName(student.name || student.firstName + ' ' + (student.lastName || '')),
                        transactions: [],
                        totalPaid: 0,
                        totalPending: 0
                      };
                      const hasPaymentPlan = studentPaymentPlans[studentId];
                      const studentTransactions = group.transactions || [];
                      const hasTransactions = studentTransactions.length > 0;
                      
                      // Get raw student data for accurate payment and program info
                      const rawStudent = rawStudentData.find(rs => 
                        (rs._id?.toString() || rs.planId?.toString()) === (studentId?.toString())
                      );
                      
                      // Get program name from multiple possible sources
                      const programName = rawStudent?.program?.programId?.name || 
                                        rawStudent?.program?.name ||
                                        student.programDetails?.name ||
                                        programs.find(p => p._id === (rawStudent?.program?.programId || student.programId))?.name ||
                                        'N/A';
                      
                      // Get accurate payment info from raw student data
                      const rawPayment = rawStudent?.payment;
                      
                      // Calculate total paid from transactions first, then fallback to student payment
                      const totalPaidFromTransactions = group.totalPaid || 0;
                      const totalPaidFromStudent = rawPayment?.paidAmount || student.payment?.paidAmount || 0;
                      const totalPaid = totalPaidFromTransactions > 0 ? totalPaidFromTransactions : totalPaidFromStudent;
                      
                      // Get total amount - prioritize program price from student's program record
                      let totalAmount = 0;
                      
                      // First, try to get from raw student's program finalPrice/basePrice (most accurate)
                      if (rawStudent?.program?.finalPrice && rawStudent.program.finalPrice > 0) {
                        totalAmount = rawStudent.program.finalPrice;
                      } else if (rawStudent?.program?.basePrice && rawStudent.program.basePrice > 0) {
                        totalAmount = rawStudent.program.basePrice;
                      }
                      // Then check raw student payment
                      else if (rawPayment?.totalAmount && rawPayment.totalAmount > 0) {
                        totalAmount = rawPayment.totalAmount;
                      } 
                      // Then check transformed student payment
                      else if (student.payment?.totalAmount && student.payment.totalAmount > 0) {
                        totalAmount = student.payment.totalAmount;
                      }
                      // Then try to get from program lookup
                      else {
                        // Get program ID from multiple sources
                        let programId = null;
                        if (rawStudent?.program?.programId) {
                          programId = typeof rawStudent.program.programId === 'object' 
                            ? rawStudent.program.programId._id || rawStudent.program.programId
                            : rawStudent.program.programId;
                        } else if (student.programId) {
                          programId = student.programId;
                        } else if (rawStudent?.programId) {
                          programId = rawStudent.programId;
                        }
                        
                        // Convert to string for comparison
                        const programIdStr = programId?.toString();
                        
                        // Find program in programs array
                        const program = programs.find(p => {
                          const pId = p._id?.toString();
                          return pId === programIdStr;
                        });
                        
                        if (program) {
                          totalAmount = program.price || program.currentPrice || program.basePrice || 0;
                        } else {
                          // Final fallback to student programDetails
                          totalAmount = student.programDetails?.price || 0;
                        }
                      }
                      
                      // Calculate pending amount - prioritize dueAmount from student payment
                      let totalPending = 0;
                      
                      // First, try to get dueAmount directly from payment record
                      if (rawPayment?.dueAmount !== undefined && rawPayment?.dueAmount !== null && rawPayment.dueAmount > 0) {
                        totalPending = rawPayment.dueAmount;
                      } else if (student.payment?.dueAmount !== undefined && student.payment?.dueAmount !== null && student.payment.dueAmount > 0) {
                        totalPending = student.payment.dueAmount;
                      } else {
                        // Calculate from totalAmount - totalPaid
                        // Make sure we have valid totalAmount
                        if (totalAmount > 0) {
                          totalPending = Math.max(0, totalAmount - totalPaid);
                        } else {
                          // If totalAmount is 0, try to get from program price
                          const programId = rawStudent?.program?.programId || student.programId;
                          const program = programs.find(p => p._id === programId);
                          if (program) {
                            const programPrice = program.price || program.currentPrice || program.basePrice || 0;
                            if (programPrice > 0) {
                              totalPending = Math.max(0, programPrice - totalPaid);
                            }
                          }
                        }
                      }
                      
                      // If still 0 or negative, check if there are pending transactions
                      // But only use transaction amounts if they're > 0 (not the auto-created 0 amount transactions)
                      if (totalPending === 0 && studentTransactions.length > 0) {
                        const pendingTxAmount = studentTransactions
                          .filter(tx => tx.status === 'pending' && (Number(tx.finalAmount) || 0) > 0)
                          .reduce((sum, tx) => sum + (Number(tx.finalAmount) || 0), 0);
                        if (pendingTxAmount > 0) {
                          totalPending = pendingTxAmount;
                        }
                      }
                      
                      // Final fallback: if we have totalAmount but no dueAmount, calculate it
                      if (totalPending === 0 && totalAmount > 0 && totalPaid < totalAmount) {
                        totalPending = totalAmount - totalPaid;
                      }
                      
                      // Debug: Log payment data to help diagnose issues
                      if (totalPending === 0 && totalAmount === 0) {
                        console.log('[FeesDetails] Payment data for student:', {
                          studentId,
                          studentName: group.name,
                          rawPayment: rawPayment ? {
                            totalAmount: rawPayment.totalAmount,
                            paidAmount: rawPayment.paidAmount,
                            dueAmount: rawPayment.dueAmount
                          } : 'null',
                          studentPayment: student.payment ? {
                            totalAmount: student.payment.totalAmount,
                            paidAmount: student.payment.paidAmount,
                            dueAmount: student.payment.dueAmount
                          } : 'null',
                          programPrice: totalAmount,
                          programId: rawStudent?.program?.programId || student.programId,
                          programName
                        });
                      }
                      
                      return (
                        <div key={studentId} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                          {/* Simple Header */}
                          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900 text-base">{formatName(group.name || student.name || `${student.firstName} ${student.lastName || ''}`)}</h4>
                                <p className="text-sm text-gray-600 mt-1">Program: {programName}</p>
                                <div className="text-sm text-gray-500 mt-1 space-y-0.5">
                                  <p>
                                    Total Paid: <span className="font-medium text-green-700">{formatCurrency(totalPaid)}</span> | 
                                    Pending: <span className="font-medium text-amber-700">{formatCurrency(totalPending)}</span>
                                  </p>
                                  {(() => {
                                    // Show discount information if available
                                    const rawProgram = rawStudent?.program;
                                    const discountApplied = rawProgram?.discountApplied || student.programDetails?.discountApplied || 0;
                                    const basePrice = rawProgram?.basePrice || student.programDetails?.basePrice || totalAmount;
                                    const finalPrice = rawProgram?.finalPrice || student.programDetails?.finalPrice || totalAmount;
                                    
                                    // Check if student has approved scholarship
                                    const studentIdStr = (studentId?.toString() || rawStudent?._id?.toString() || rawStudent?.planId?.toString());
                                    const studentScholarship = scholarshipApplications.find(app => 
                                      app.studentId?.toString() === studentIdStr && app.status === 'approved'
                                    );
                                    
                                    if (discountApplied > 0 && basePrice > finalPrice) {
                                      return (
                                        <div className="space-y-1">
                                          <p className="text-xs text-gray-400">
                                            Original: <span className="line-through">{formatCurrency(basePrice)}</span> | 
                                            Discount: <span className="text-green-600 font-medium">-{formatCurrency(discountApplied)}</span> | 
                                            Final: <span className="font-medium">{formatCurrency(finalPrice)}</span>
                                          </p>
                                          {studentScholarship && (
                                            <p className="text-xs text-purple-600 font-medium flex items-center gap-1">
                                              <FaGraduationCap className="text-xs" />
                                              Scholarship Applied: {typeof studentScholarship.scholarshipOfferId === 'object' 
                                                ? studentScholarship.scholarshipOfferId.name 
                                                : 'Scholarship'}
                                              {typeof studentScholarship.scholarshipOfferId === 'object' && studentScholarship.scholarshipOfferId.discountPercentage && (
                                                <span className="text-purple-500">({studentScholarship.scholarshipOfferId.discountPercentage}%)</span>
                                              )}
                                            </p>
                                          )}
                                          <p className="text-xs text-blue-500 italic">
                                            💡 Discounts and payment plans are automatically applied
                                          </p>
                                        </div>
                                      );
                                    }
                                    
                                    // Show scholarship even if no other discount
                                    if (studentScholarship) {
                                      return (
                                        <p className="text-xs text-purple-600 font-medium flex items-center gap-1">
                                          <FaGraduationCap className="text-xs" />
                                          Scholarship Applied: {typeof studentScholarship.scholarshipOfferId === 'object' 
                                            ? studentScholarship.scholarshipOfferId.name 
                                            : 'Scholarship'}
                                          {typeof studentScholarship.scholarshipOfferId === 'object' && studentScholarship.scholarshipOfferId.discountPercentage && (
                                            <span className="text-purple-500">({studentScholarship.scholarshipOfferId.discountPercentage}%)</span>
                                          )}
                                        </p>
                                      );
                                    }
                                    
                                    return null;
                                  })()}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                {/* "Assign Plan" button removed - students choose plans during enrollment */}
                                {/* Plans are auto-assigned if student doesn't select (defaults to first plan) */}
                                {hasPaymentPlan && (
                                  <>
                                    <button
                                      onClick={() => {
                                        fetchPaymentSchedule(studentId);
                                        setShowPaymentScheduleModal(studentId);
                                      }}
                                      className="px-3 py-1.5 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 flex items-center gap-1.5"
                                      title="View Payment Schedule"
                                    >
                                      <FiCalendar size={14} />
                                      Schedule
                                    </button>
                                    <button
                                      onClick={() => {
                                        fetchReminderHistory(studentId);
                                        setShowReminderHistoryModal(studentId);
                                      }}
                                      className="px-3 py-1.5 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700 flex items-center gap-1.5"
                                      title="View Reminder History"
                                    >
                                      <FiClock size={14} />
                                      Reminders
                                    </button>
                                  </>
                                )}
                                <button
                                  onClick={async () => {
                                    try {
                                      await HttpInterceptor.post('/api/fee-plan/reminders/send', {
                                        studentId: studentId
                                      });
                                      toast.success('Reminder sent successfully');
                                    } catch (err) {
                                      const pendingTx = studentTransactions.find(tx => tx.status === 'pending');
                                      if (pendingTx) {
                                        handleSendReminder(pendingTx._id);
                                      } else {
                                        toast.error(err.response?.data?.message || 'Failed to send reminder');
                                      }
                                    }
                                  }}
                                  className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1.5"
                                  title="Send Reminder"
                                >
                                  <FiSend size={14} />
                                  Send Reminder
                                </button>
                              </div>
                            </div>
                          </div>
                          {/* Transactions Table */}
                          {hasTransactions ? (
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      <input
                                        type="checkbox"
                                        checked={studentTransactions.length > 0 && studentTransactions.every(tx => selectedTransactions.includes(tx._id))}
                                        onChange={() => {
                                          const allSelected = studentTransactions.every(tx => selectedTransactions.includes(tx._id));
                                          if (allSelected) {
                                            setSelectedTransactions(prev => 
                                              prev.filter(id => !studentTransactions.some(tx => tx._id === id))
                                            );
                                          } else {
                                            const toAdd = studentTransactions
                                              .filter(tx => !selectedTransactions.includes(tx._id))
                                              .map(tx => tx._id);
                                            setSelectedTransactions(prev => [...prev, ...toAdd]);
                                          }
                                        }}
                                        className="h-4 w-4 text-orange-600 focus:ring-teal-500 border-gray-300 rounded"
                                      />
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{isConsultant ? 'Service' : 'Program'}</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{isConsultant ? 'Method' : 'Payment Method'}</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {studentTransactions.map(transaction => (
                                    <>
                                      <tr key={transaction._id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          <input
                                            type="checkbox"
                                            checked={selectedTransactions.includes(transaction._id)}
                                            onChange={() => handleSelectTransaction(transaction._id)}
                                            className="h-4 w-4 text-orange-600 focus:ring-teal-500 border-gray-300 rounded"
                                          />
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                                          {formatDateUtil(transaction.date || transaction.createdAt || transaction.dueDate, dateTimeSettings.dateFormat)}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                                          {transaction.invoiceNo}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                                          {transaction.programId?.name || programName || 'Unknown'}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                                          {(() => {
                                            // If transaction amount is 0 (auto-created transaction), show the actual pending amount
                                            let displayAmount = transaction.finalAmount || 0;
                                            if (displayAmount === 0 && transaction.status === 'pending') {
                                              // Get actual pending amount for this student
                                              const studentPending = totalPending || 0;
                                              if (studentPending > 0) {
                                                return (
                                                  <span className="font-medium">{formatCurrency(studentPending)}</span>
                                                );
                                              }
                                            }
                                            
                                            // Show discount breakdown if discount was applied
                                            const baseAmount = transaction.baseAmount || 0;
                                            const discountBreakdown = transaction.discountBreakdown || [];
                                            const hasDiscount = discountBreakdown.length > 0;
                                            
                                            return (
                                              <div className="flex flex-col">
                                                <div className="flex items-center gap-1">
                                                  <span className="font-medium">{formatCurrency(displayAmount)}</span>
                                                  {transaction.installmentNumber && (
                                                    <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded flex items-center gap-1">
                                                      <FiCreditCard size={10} />
                                                      Inst. {transaction.installmentNumber}
                                                    </span>
                                                  )}
                                                  {transaction.scholarshipApplied && (
                                                    <span className="text-xs bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded flex items-center gap-1">
                                                      <FaGraduationCap size={10} />
                                                      Scholarship
                                                    </span>
                                                  )}
                                                </div>
                                                {hasDiscount && (
                                                  <div className="text-xs text-gray-500 mt-0.5 space-y-0.5">
                                                    <div className="line-through">{formatCurrency(baseAmount)}</div>
                                                    {discountBreakdown.map((item, idx) => (
                                                      <div key={idx} className={`flex items-center gap-1 ${
                                                        item.type === 'Scholarship' ? 'text-purple-600' :
                                                        item.type === 'Promo Code' ? 'text-blue-600' :
                                                        'text-green-600'
                                                      }`}>
                                                        <span>-{formatCurrency(item.amount)}</span>
                                                        <span className={`px-1 py-0.5 rounded text-[10px] ${
                                                          item.type === 'Scholarship' ? 'bg-purple-100 text-purple-800' :
                                                          item.type === 'Promo Code' ? 'bg-blue-100 text-blue-800' :
                                                          'bg-green-100 text-green-800'
                                                        }`}>
                                                          {item.type}
                                                        </span>
                                                        <span className="text-blue-500 italic">(auto)</span>
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })()}
                                          {transaction.lateFee > 0 && (
                                            <span className="text-xs text-rose-600 ml-1 block mt-0.5">
                                              (+{formatCurrency(transaction.lateFee)} late fee)
                                            </span>
                                          )}
                                          {transaction.refundedAmount > 0 && (
                                            <span className="text-xs text-red-600 ml-1 block mt-0.5">
                                              (-{formatCurrency(transaction.refundedAmount)} refunded)
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                                          <div className="flex items-center gap-1">
                                            {transaction.paymentMethod === 'UPI' && <FiCreditCard size={14} className="text-blue-600" />}
                                            {transaction.paymentMethod === 'Cash' && <FaRupeeSign size={12} className="text-green-600" />}
                                            {transaction.paymentMethod === 'Credit Card' && <FiCreditCard size={14} className="text-purple-600" />}
                                            {transaction.paymentMethod === 'Debit Card' && <FiCreditCard size={14} className="text-indigo-600" />}
                                            {transaction.paymentMethod === 'Bank Transfer' && <FiCreditCard size={14} className="text-orange-600" />}
                                            <span>{transaction.paymentMethod || 'Other'}</span>
                                          </div>
                                          {transaction.paymentMethodId && (
                                            <span className="text-xs text-gray-500 block mt-0.5">ID: {transaction.paymentMethodId}</span>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          <span className={`px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full ${
                                            transaction.status === 'completed' ? 'bg-green-100 text-green-800' :
                                            transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                            transaction.status === 'refunded' ? 'bg-blue-100 text-blue-800' :
                                            transaction.status === 'partially_refunded' ? 'bg-orange-100 text-orange-800' :
                                            'bg-red-100 text-red-800'
                                          }`}>
                                            {formatPaymentStatus(transaction.status)}
                                          </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                                          <div className="flex justify-end gap-2">
                                            <button
                                              onClick={() => {
                                                const newExpanded = new Set(expandedTransactions);
                                                if (newExpanded.has(transaction._id)) {
                                                  newExpanded.delete(transaction._id);
                                                } else {
                                                  newExpanded.add(transaction._id);
                                                }
                                                setExpandedTransactions(newExpanded);
                                              }}
                                              className="text-gray-600 hover:text-gray-900"
                                              title="Toggle Details"
                                            >
                                              {expandedTransactions.has(transaction._id) ? (
                                                <FiChevronUp size={16} />
                                              ) : (
                                                <FiChevronDown size={16} />
                                              )}
                                            </button>
                                            <button
                                              onClick={() => handleSendInvoice(transaction._id)}
                                              className="text-orange-600 hover:text-teal-900"
                                              disabled={isLoading || transaction.status === 'refunded'}
                                              title="Send Invoice"
                                            >
                                              <FiSend size={16} />
                                            </button>
                                            {transaction.status === 'completed' && (
                                              <button
                                                onClick={() => {
                                                  setRefundData({
                                                    transactionId: transaction._id,
                                                    amount: transaction.finalAmount - (transaction.refundedAmount || 0),
                                                    reason: '',
                                                    isPartial: false
                                                  });
                                                  setShowRefundForm(true);
                                                }}
                                                className="text-rose-600 hover:text-rose-900"
                                                title="Refund"
                                              >
                                                <FaRupeeSign size={16} />
                                              </button>
                                            )}
                                            {transaction.status === 'pending' && (
                                              <button
                                                onClick={() => handleSendReminder(transaction._id)}
                                                className="text-orange-600 hover:text-orange-900"
                                                title="Send Reminder"
                                              >
                                                <FiClock size={16} />
                                              </button>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                      {expandedTransactions.has(transaction._id) && (
                                        <tr className="bg-gray-50">
                                          <td colSpan={8} className="px-4 py-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                              <div>
                                                <h4 className="font-semibold text-gray-700 mb-2">Transaction Details</h4>
                                                <div className="space-y-1 text-gray-600">
                                                  <p><span className="font-medium">Base Amount:</span> {formatCurrency(transaction.baseAmount || 0)}</p>
                                                  <p><span className="font-medium">Final Amount:</span> {formatCurrency(transaction.finalAmount || 0)}</p>
                                                  {transaction.lateFee > 0 && (
                                                    <p><span className="font-medium">Late Fee:</span> <span className="text-rose-600">{formatCurrency(transaction.lateFee)}</span></p>
                                                  )}
                                                  {transaction.refundedAmount > 0 && (
                                                    <p><span className="font-medium">Refunded:</span> <span className="text-red-600">{formatCurrency(transaction.refundedAmount)}</span></p>
                                                  )}
                                                  {transaction.refundedReason && (
                                                    <p><span className="font-medium">Refund Reason:</span> {transaction.refundedReason}</p>
                                                  )}
                                                  {transaction.notes && (
                                                    <p><span className="font-medium">Notes:</span> {transaction.notes}</p>
                                                  )}
                                                </div>
                                              </div>
                                              <div>
                                                <h4 className="font-semibold text-gray-700 mb-2">Discount & Benefits</h4>
                                                {transaction.discountBreakdown && transaction.discountBreakdown.length > 0 ? (
                                                  <div className="space-y-1">
                                                    {transaction.discountBreakdown.map((item, idx) => (
                                                      <div key={idx} className={`p-2 rounded ${
                                                        item.type === 'Scholarship' ? 'bg-purple-50 border border-purple-200' :
                                                        item.type === 'Promo Code' ? 'bg-blue-50 border border-blue-200' :
                                                        'bg-green-50 border border-green-200'
                                                      }`}>
                                                        <div className="flex items-center justify-between">
                                                          <span className={`font-medium ${
                                                            item.type === 'Scholarship' ? 'text-purple-800' :
                                                            item.type === 'Promo Code' ? 'text-blue-800' :
                                                            'text-green-800'
                                                          }`}>
                                                            {item.type}: {item.name}
                                                          </span>
                                                          <span className="text-gray-700">{formatCurrency(item.amount)} ({item.percentage}%)</span>
                                                        </div>
                                                      </div>
                                                    ))}
                                                  </div>
                                                ) : (
                                                  <p className="text-gray-500">No discounts applied</p>
                                                )}
                                                {transaction.installmentNumber && (
                                                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                                                    <p className="font-medium text-blue-800">Installment Plan</p>
                                                    <p className="text-sm text-gray-600">Installment #{transaction.installmentNumber}</p>
                                                  </div>
                                                )}
                                                {transaction.scholarshipApplied && (
                                                  <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded">
                                                    <p className="font-medium text-purple-800 flex items-center gap-1">
                                                      <FaGraduationCap size={14} />
                                                      Scholarship Applied
                                                    </p>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="px-6 py-8 text-center bg-gray-50">
                              {totalPending > 0 ? (
                                <div>
                                  <p className="text-gray-600 mb-2">
                                    <span className="font-medium">Pending Amount: {formatCurrency(totalPending)}</span>
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    Transaction will be created automatically. Please refresh the page.
                                  </p>
                                </div>
                              ) : (
                                <p className="text-gray-500">No transactions yet</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </TabPanel>

          <TabPanel>
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-800">Financial Reports</h2>
              <p className="text-sm text-gray-500">Analyze your financial performance and generate detailed reports</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Income Overview</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="income" fill="#10B981" name="Income" />
                      <Bar dataKey="refunds" fill="#EF4444" name="Refunds" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Status</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={(() => {
                          const paymentStatusData = [
                            { name: 'Completed', value: transactions.filter(t => t.status === 'completed').length },
                            { name: 'Pending', value: transactions.filter(t => t.status === 'pending').length },
                            { name: 'Refunded', value: transactions.filter(t => t.status === 'refunded').length },
                            { name: 'Failed', value: transactions.filter(t => t.status === 'failed').length }
                          ];
                          // Filter out zero values to prevent overlapping labels
                          return paymentStatusData.filter(item => item.value > 0);
                        })()}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {(() => {
                          const paymentStatusData = [
                            { name: 'Completed', value: transactions.filter(t => t.status === 'completed').length },
                            { name: 'Pending', value: transactions.filter(t => t.status === 'pending').length },
                            { name: 'Refunded', value: transactions.filter(t => t.status === 'refunded').length },
                            { name: 'Failed', value: transactions.filter(t => t.status === 'failed').length }
                          ];
                          const filteredData = paymentStatusData.filter(item => item.value > 0);
                          return filteredData.map((entry, index) => {
                            // Map original index to get correct color
                            const originalIndex = paymentStatusData.findIndex(item => item.name === entry.name);
                            return (
                              <Cell key={`cell-${index}`} fill={COLORS[originalIndex % COLORS.length]} />
                            );
                          });
                        })()}
                      </Pie>
                      <Tooltip />
                      <Legend 
                        formatter={(value) => {
                          const total = transactions.length;
                          const count = transactions.filter(t => {
                            if (value === 'Completed') return t.status === 'completed';
                            if (value === 'Pending') return t.status === 'pending';
                            if (value === 'Refunded') return t.status === 'refunded';
                            if (value === 'Failed') return t.status === 'failed';
                            return false;
                          }).length;
                          const percent = total > 0 ? ((count / total) * 100).toFixed(0) : 0;
                          return `${value}: ${percent}%`;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue Trend</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="income" stroke="#10B981" name="Income" />
                      <Line type="monotone" dataKey="pending" stroke="#F59E0B" name="Pending" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Detailed Report</h3>
                <div className="flex gap-2">
                  <select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="income">Income Statement</option>
                    <option value="outstanding">Outstanding Fees</option>
                    <option value="projection">Revenue Projection</option>
                    <option value="late-fees">Late Fees Collected</option>
                  </select>
                  <button
                    onClick={fetchFinancialReport}
                    className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm"
                  >
                    <FiDownload size={16} />
                    Generate
                  </button>
                </div>
              </div>

              {financialReport ? (
                <div className="mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {financialReport.summary && Object.entries(financialReport.summary).map(([key, value]) => {
                      // Check if this is a count field (should not be formatted as currency)
                      const isCountField = key.toLowerCase().includes('count');
                      return (
                        <div key={key} className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="text-sm font-medium text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1')}</h4>
                          <p className="text-xl font-semibold">
                            {typeof value === 'number' 
                              ? (isCountField ? value.toLocaleString() : formatCurrency(value))
                              : value}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {financialReport.headers?.map(header => (
                            <th key={header} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {financialReport.rows?.map((row, i) => (
                          <tr key={i}>
                            {row.map((cell, j) => {
                              // Check if this column is an amount column (should be formatted as currency)
                              const header = financialReport.headers?.[j];
                              const isAmountColumn = header && (header.toLowerCase().includes('amount') || header.toLowerCase().includes('fee') || header.toLowerCase().includes('revenue'));
                              
                              return (
                                <td key={j} className="px-4 py-3 whitespace-nowrap text-sm">
                                  {typeof cell === 'number' 
                                    ? (isAmountColumn ? formatCurrency(cell) : cell.toLocaleString())
                                    : (cell || '-')}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  Select report type and click "Generate" to view financial report
                </div>
              )}
            </div>
          </TabPanel>

          <TabPanel>
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-800">{isConsultant ? 'Service Reminders' : 'Payment Reminders'}</h2>
              <p className="text-sm text-gray-500">Manage automated reminders for pending payments</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Reminder Logs</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowReminderLogsFilters(!showReminderLogsFilters)}
                    className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm"
                  >
                    <FiFilter size={16} />
                    Filters
                  </button>
                  <button
                    onClick={() => setShowReminderSettings(!showReminderSettings)}
                    className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm"
                  >
                    <FiSettings size={16} />
                    Settings
                  </button>
                </div>
              </div>

              {/* Filters Section */}
              {showReminderLogsFilters && (
                <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={reminderLogsFilters.status}
                        onChange={(e) => {
                          setReminderLogsFilters({ ...reminderLogsFilters, status: e.target.value });
                          setReminderLogsPagination({ ...reminderLogsPagination, page: 1 });
                        }}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        <option value="">All Statuses</option>
                        <option value="sent">Sent</option>
                        <option value="failed">Failed</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{isConsultant ? 'Client' : 'Student'}</label>
                      <select
                        value={reminderLogsFilters.studentId}
                        onChange={(e) => {
                          setReminderLogsFilters({ ...reminderLogsFilters, studentId: e.target.value });
                          setReminderLogsPagination({ ...reminderLogsPagination, page: 1 });
                        }}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        <option value="">{isConsultant ? 'All Clients' : 'All Students'}</option>
                        {students.filter(s => s.status === 'active').map(student => {
                          // Use Student document _id (not User _id)
                          const studentDocId = student._id || student.planId;
                          return (
                            <option key={studentDocId} value={studentDocId}>{formatName(student.name)}</option>
                          );
                        })}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                      <DatePicker
                        selected={reminderLogsFilters.startDate}
                        onChange={(date) => {
                          setReminderLogsFilters({ ...reminderLogsFilters, startDate: date });
                          setReminderLogsPagination({ ...reminderLogsPagination, page: 1 });
                        }}
                        dateFormat="dd/MM/yyyy"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholderText="Select start date"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                      <DatePicker
                        selected={reminderLogsFilters.endDate}
                        onChange={(date) => {
                          setReminderLogsFilters({ ...reminderLogsFilters, endDate: date });
                          setReminderLogsPagination({ ...reminderLogsPagination, page: 1 });
                        }}
                        dateFormat="dd/MM/yyyy"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholderText="Select end date"
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setReminderLogsFilters({
                          status: '',
                          studentId: '',
                          startDate: null,
                          endDate: null
                        });
                        setReminderLogsPagination({ ...reminderLogsPagination, page: 1 });
                      }}
                      className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              )}

              {/* Bulk Actions */}
              {selectedReminderLogs.length > 0 && (
                <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-center justify-between">
                  <span className="text-sm text-orange-800">
                    {selectedReminderLogs.length} reminder log(s) selected
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setConfirmConfig({
                          isOpen: true,
                          title: 'Delete Reminder Logs',
                          message: `Are you sure you want to delete ${selectedReminderLogs.length} reminder log(s)? This action cannot be undone.`,
                          variant: 'danger',
                          onConfirm: handleBulkDeleteReminderLogs
                        });
                      }}
                      className="px-3 py-1.5 text-sm text-red-700 bg-red-100 border border-red-300 rounded-md hover:bg-red-200"
                    >
                      Delete Selected
                    </button>
                    <button
                      onClick={() => setSelectedReminderLogs([])}
                      className="px-3 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Clear Selection
                    </button>
                  </div>
                </div>
              )}

              {/* Export Button */}
              <div className="mb-4 flex justify-end">
                <button
                  onClick={exportReminderLogsToCSV}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm"
                >
                  <FiDownload size={16} />
                  Export CSV
                </button>
              </div>

              {reminderLogs.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <input
                              type="checkbox"
                              checked={selectedReminderLogs.length === reminderLogs.length && reminderLogs.length > 0}
                              onChange={handleSelectAllReminderLogs}
                              className="rounded border-gray-300"
                            />
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {reminderLogs.map(log => (
                          <tr key={log._id}>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedReminderLogs.includes(log._id)}
                                onChange={() => handleSelectReminderLog(log._id)}
                                className="rounded border-gray-300"
                              />
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              {formatDateTime(log.sentAt, dateTimeSettings.dateFormat, dateTimeSettings.timeFormat)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              {log.studentName}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                              {log.invoiceNo}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              {formatCurrency(log.amount)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full ${
                                log.status === 'sent' ? 'bg-green-100 text-green-800' :
                                log.status === 'failed' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {log.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              <button
                                onClick={() => {
                                  setConfirmConfig({
                                    isOpen: true,
                                    title: 'Delete Reminder Log',
                                    message: 'Are you sure you want to delete this reminder log? This action cannot be undone.',
                                    variant: 'danger',
                                    onConfirm: () => handleDeleteReminderLog(log._id)
                                  });
                                }}
                                className="text-red-600 hover:text-red-800"
                                title="Delete"
                              >
                                <FiTrash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {reminderLogsPagination.totalPages > 1 && (
                    <div className="mt-4 flex items-center justify-between">
                      <div className="text-sm text-gray-700">
                        Showing {((reminderLogsPagination.page - 1) * reminderLogsPagination.limit) + 1} to{' '}
                        {Math.min(reminderLogsPagination.page * reminderLogsPagination.limit, reminderLogsPagination.total)} of{' '}
                        {reminderLogsPagination.total} entries
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setReminderLogsPagination({ ...reminderLogsPagination, page: reminderLogsPagination.page - 1 })}
                          disabled={reminderLogsPagination.page === 1}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          Previous
                        </button>
                        <span className="px-3 py-1 text-sm text-gray-700">
                          Page {reminderLogsPagination.page} of {reminderLogsPagination.totalPages}
                        </span>
                        <button
                          onClick={() => setReminderLogsPagination({ ...reminderLogsPagination, page: reminderLogsPagination.page + 1 })}
                          disabled={reminderLogsPagination.page >= reminderLogsPagination.totalPages}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">No reminder logs found</div>
              )}
            </div>

            {/* Reminder Settings */}
            {showReminderSettings && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Reminder Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Days Before Due Date</label>
                    <input
                      type="number"
                      value={reminderSettings.daysBeforeDue}
                      onChange={(e) => setReminderSettings({
                        ...reminderSettings,
                        daysBeforeDue: parseInt(e.target.value)
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Days After Due Date</label>
                    <input
                      type="text"
                      value={reminderSettings.daysAfterDue.join(', ')}
                      onChange={(e) => setReminderSettings({
                        ...reminderSettings,
                        daysAfterDue: e.target.value.split(',').map(num => parseInt(num.trim())).filter(num => !isNaN(num))
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="1, 3, 7"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Active</label>
                    <select
                      value={reminderSettings.active}
                      onChange={(e) => setReminderSettings({
                        ...reminderSettings,
                        active: e.target.value === 'true'
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
                    <select
                      value={reminderSettings.templateId}
                      onChange={(e) => setReminderSettings({
                        ...reminderSettings,
                        templateId: e.target.value
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="">Select Template</option>
                      {reminderTemplates.map(template => (
                        <option key={template._id} value={template._id}>{template.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2 flex justify-end gap-3">
                    <button
                      onClick={() => setShowReminderSettings(false)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveReminderSettings}
                      className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg"
                    >
                      Save Settings
                    </button>
                  </div>
                </div>
              </div>
            )}
          </TabPanel>

          {!isConsultant && (
            <TabPanel>
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-800">Payment Settings</h2>
                <p className="text-sm text-gray-500">Configure payment-related settings and rules</p>
              </div>

              {/* Late Fee Settings */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Late Fee Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fee Type</label>
                  <select
                    value={lateFeeSettings.feeType}
                    onChange={(e) => setLateFeeSettings({
                      ...lateFeeSettings,
                      feeType: e.target.value
                    })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {lateFeeSettings.feeType === 'percentage' ? 'Percentage' : 'Amount'}
                  </label>
                  <input
                    type="number"
                    value={lateFeeSettings.amount}
                    onChange={(e) => setLateFeeSettings({
                      ...lateFeeSettings,
                      amount: parseFloat(e.target.value)
                    })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    min="0"
                    step={lateFeeSettings.feeType === 'percentage' ? "0.1" : "1"}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grace Period (days)</label>
                  <input
                    type="number"
                    value={lateFeeSettings.gracePeriod}
                    onChange={(e) => setLateFeeSettings({
                      ...lateFeeSettings,
                      gracePeriod: parseInt(e.target.value)
                    })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Maximum Fee</label>
                  <input
                    type="number"
                    value={lateFeeSettings.maxFee}
                    onChange={(e) => setLateFeeSettings({
                      ...lateFeeSettings,
                      maxFee: parseFloat(e.target.value)
                    })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    min="0"
                    step="1"
                    disabled={lateFeeSettings.feeType === 'fixed'}
                  />
                </div>
                <div className="md:col-span-2 flex justify-end">
                  <button
                    onClick={handleSaveLateFeeSettings}
                    className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg"
                  >
                    Save Settings
                  </button>
                </div>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Methods</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center">
                      <FaRupeeSign className="text-orange-500" />
                    </div>
                    <div>
                      <h4 className="font-medium">Credit/Debit Card</h4>
                      <p className="text-sm text-gray-500">Accept payments via cards</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                      <FiUser className="text-green-500" />
                    </div>
                    <div>
                      <h4 className="font-medium">Bank Transfer</h4>
                      <p className="text-sm text-gray-500">Direct bank transfers</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                      <FiCheckCircle className="text-purple-500" />
                    </div>
                    <div>
                      <h4 className="font-medium">UPI Payments</h4>
                      <p className="text-sm text-gray-500">Instant UPI transfers</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                  </label>
                </div>
              </div>
            </div>
            </TabPanel>
          )}
        </Tabs>

        {/* Assign Payment Plan Modal */}
        {showAssignPlanModal && selectedStudentForPlan && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">Assign Payment Plan to {selectedStudentForPlan.name || selectedStudentForPlan.personalDetails?.name}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Payment Plan</label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAssignPaymentPlan(e.target.value);
                      }
                    }}
                  >
                    <option value="">Select a payment plan...</option>
                    {paymentPlans
                      .filter(plan => {
                        // Filter plans that match the student's program
                        // Get the student's program ID from the raw student data
                        const studentId = selectedStudentForPlan._id || selectedStudentForPlan.planId;
                        const rawStudent = rawStudentData.find(s => {
                          const sId = s._id?.toString() || s.planId?.toString();
                          const targetId = studentId?.toString();
                          return sId === targetId;
                        });
                        if (!rawStudent) return false;
                        
                        // Get student program ID (handle both object and string formats)
                        let studentProgramId = null;
                        if (rawStudent.program?.programId) {
                          studentProgramId = typeof rawStudent.program.programId === 'object' 
                            ? rawStudent.program.programId._id?.toString() || rawStudent.program.programId.toString()
                            : rawStudent.program.programId.toString();
                        } else if (rawStudent.programId) {
                          studentProgramId = typeof rawStudent.programId === 'object'
                            ? rawStudent.programId._id?.toString() || rawStudent.programId.toString()
                            : rawStudent.programId.toString();
                        }
                        
                        // Get plan program ID (should be a string from API, but handle object too)
                        const planProgramId = plan.programId?._id?.toString() || plan.programId?.toString() || plan.programId;
                        
                        return planProgramId && studentProgramId && planProgramId.toString() === studentProgramId.toString();
                      })
                      .map(plan => (
                        <option key={plan._id} value={plan._id}>
                          {plan.name} ({plan.numberOfInstallments} installments)
                        </option>
                      ))}
                  </select>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setShowAssignPlanModal(false);
                      setSelectedStudentForPlan(null);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment Schedule Modal */}
        {showPaymentScheduleModal && paymentSchedule && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Payment Schedule - {paymentSchedule.student?.name}</h3>
                <button
                  onClick={() => {
                    setShowPaymentScheduleModal(null);
                    setPaymentSchedule(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FiX size={24} />
                </button>
              </div>
              {paymentSchedule.paymentPlan && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600">
                    Plan: <span className="font-medium">{paymentSchedule.paymentPlan.name}</span> ({paymentSchedule.paymentPlan.numberOfInstallments} installments)
                  </p>
                </div>
              )}
              {paymentSchedule.schedule && paymentSchedule.schedule.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Percentage</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paymentSchedule.schedule.map((installment) => (
                      <tr key={installment.installmentNumber}>
                        <td className="px-4 py-3 text-sm">{installment.installmentNumber}</td>
                        <td className="px-4 py-3 text-sm">{formatDateUtil(installment.dueDate, dateTimeSettings.dateFormat)}</td>
                        <td className="px-4 py-3 text-sm">{formatCurrency(installment.amount)}</td>
                        <td className="px-4 py-3 text-sm">{installment.percentage}%</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full ${
                            installment.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {installment.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-gray-500 text-center py-8">No payment schedule available</p>
              )}
            </div>
          </div>
        )}

        {/* Reminder History Modal */}
        {showReminderHistoryModal && reminderHistory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Reminder History - {reminderHistory.student?.name}</h3>
                <button
                  onClick={() => {
                    setShowReminderHistoryModal(null);
                    setReminderHistory(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FiX size={24} />
                </button>
              </div>
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Payment Status: <span className="font-medium">{reminderHistory.paymentStatus}</span> | 
                  Total Due: <span className="font-medium">{formatCurrency(reminderHistory.totalDue || 0)}</span>
                </p>
              </div>
              {reminderHistory.reminders && reminderHistory.reminders.length > 0 ? (
                <div className="space-y-3">
                  {reminderHistory.reminders.map((reminder, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{reminder.message}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatDateTime(reminder.date, dateTimeSettings.dateFormat, dateTimeSettings.timeFormat)}
                          </p>
                          {reminder.invoiceNo && (
                            <p className="text-xs text-gray-500">Invoice: {reminder.invoiceNo}</p>
                          )}
                          {reminder.amount && (
                            <p className="text-xs text-gray-500">Amount: {formatCurrency(reminder.amount)}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            reminder.sent ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {reminder.sent ? 'Sent' : 'Pending'}
                          </span>
                          {reminder.status && (
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              reminder.status === 'sent' ? 'bg-blue-100 text-blue-800' : 
                              reminder.status === 'failed' ? 'bg-red-100 text-red-800' : 
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {reminder.status}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No reminder history available</p>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Unified Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmConfig.isOpen}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        variant={confirmConfig.variant}
      />
    </ErrorBoundary>
  );
}

export default FeesDetails;